/**
 * SearchPanelContent - Pure search UI body for desktop right panel
 * Extracted from mobile SearchSuggestionsOverlay - same UI, no drawer shell.
 * @version 1.0.0
 */
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Search, X, ArrowRight, Building2, Tag, Layers, Clock, Trash2 } from 'lucide-react';
import type { ActivePill } from '../../../hooks/useDiscoveryFiltersState';
import { getIndustryLabel, getCategoryLabel, type FacetItem } from '../types';

const RECENT_SEARCHES_KEY = 'klout-discover-recent-searches';
const MAX_RECENT_SEARCHES = 6;
const RELEVANCE_DEBOUNCE_MS = 700;

export interface SearchPanelContentProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onExecuteSearch: (query?: string) => void;
  selectedIndustryIds: string[];
  selectedCategoryIds: string[];
  selectedSubcategoryIds: string[];
  onAddIndustry: (id: string) => void;
  onRemoveIndustry: (id: string) => void;
  onAddCategory: (id: string) => void;
  onRemoveCategory: (id: string) => void;
  onAddSubcategory: (id: string) => void;
  onExecuteWithSelection: (industryId?: string, categoryId?: string, subcategoryId?: string, query?: string) => void;
  industryFacets: FacetItem[];
  categoryFacets: FacetItem[];
  subcategoryFacets: FacetItem[];
  creatorIndustryIds?: string[];
}

function computeIndustryRelevance(industry: FacetItem, query: string): number {
  if (!query.trim()) return industry.count;
  const q = query.toLowerCase().trim();
  let score = industry.count;
  if (industry.label.toLowerCase().includes(q)) score += 1000;
  const matchingMinis = industry.miniItems.filter((m: string) => m.toLowerCase().includes(q)).length;
  score += matchingMinis * 50;
  return score;
}

function computeCategoryRelevance(category: FacetItem, query: string): number {
  if (!query.trim()) return category.count;
  const q = query.toLowerCase().trim();
  let score = category.count;
  if (category.label.toLowerCase().includes(q)) score += 500;
  return score;
}

export const SearchPanelContent: React.FC<SearchPanelContentProps> = ({
  searchQuery,
  onSearchQueryChange,
  onExecuteSearch,
  selectedIndustryIds,
  selectedCategoryIds,
  selectedSubcategoryIds,
  onAddIndustry,
  onRemoveIndustry,
  onAddCategory,
  onRemoveCategory,
  onAddSubcategory,
  onExecuteWithSelection,
  industryFacets,
  categoryFacets,
  subcategoryFacets,
  creatorIndustryIds = [],
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [recentSearches, setRecentSearches] = React.useState<string[]>([]);
  const [debouncedQuery, setDebouncedQuery] = React.useState(searchQuery);
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery), RELEVANCE_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) setRecentSearches(JSON.parse(stored));
    } catch { /* localStorage unavailable */ }
  }, []);

  // Auto-focus
  React.useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const saveToRecent = (query: string) => {
    if (!query.trim()) return;
    const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, MAX_RECENT_SEARCHES);
    setRecentSearches(updated);
    try { localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated)); } catch { /* localStorage unavailable */ }
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem(RECENT_SEARCHES_KEY); } catch { /* localStorage unavailable */ }
  };

  const hasIndustry = selectedIndustryIds.length > 0;
  const hasCategory = selectedCategoryIds.length > 0;
  const hasTyping = searchQuery.trim().length > 0;

  const sortedIndustries = React.useMemo(() => {
    return [...industryFacets].sort((a, b) => computeIndustryRelevance(b, debouncedQuery) - computeIndustryRelevance(a, debouncedQuery));
  }, [industryFacets, debouncedQuery]);

  const recommendedIndustry = React.useMemo(() => {
    if (creatorIndustryIds.length === 0 || sortedIndustries.length === 0) return null;
    for (const id of creatorIndustryIds) {
      const found = sortedIndustries.find((ind) => ind.id === id && ind.count > 0);
      if (found) return found;
    }
    return null;
  }, [creatorIndustryIds, sortedIndustries]);

  const queryBestIndustry = React.useMemo(() => {
    if (!debouncedQuery.trim() || sortedIndustries.length === 0) return null;
    const best = sortedIndustries[0];
    return computeIndustryRelevance(best, debouncedQuery) > best.count ? best : null;
  }, [sortedIndustries, debouncedQuery]);

  const pinnedIndustry = hasTyping ? queryBestIndustry : recommendedIndustry;
  const pinnedLabel = hasTyping && queryBestIndustry ? `Top match for "${debouncedQuery.trim()}"` : recommendedIndustry ? 'Recommended for you' : null;

  const pinnedIndustryCategories = React.useMemo(() => {
    if (!pinnedIndustry) return [];
    return [...categoryFacets].filter((c) => c.count > 0).sort((a, b) => computeCategoryRelevance(b, debouncedQuery) - computeCategoryRelevance(a, debouncedQuery));
  }, [pinnedIndustry, categoryFacets, debouncedQuery]);

  const handleRecentClick = (query: string) => {
    onSearchQueryChange(query);
    saveToRecent(query);
    onExecuteSearch(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { saveToRecent(searchQuery); onExecuteSearch(searchQuery); }
  };

  const handleSearchExecute = () => { saveToRecent(searchQuery); onExecuteSearch(searchQuery); };

  const handleHeadlineClick = (id: string, type: 'industry' | 'category' | 'subcategory') => {
    if (type === 'industry') onAddIndustry(id);
    else if (type === 'category') onAddCategory(id);
    else { onAddSubcategory(id); saveToRecent(searchQuery); onExecuteSearch(searchQuery); }
  };

  const handleCategoryGridClick = (categoryId: string, industryId?: string) => {
    saveToRecent(searchQuery);
    onExecuteWithSelection(industryId, categoryId, undefined, searchQuery);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search input */}
      <div className="shrink-0 px-4 pt-3 pb-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search deals..."
            className="w-full pl-6 pr-8 py-1.5 bg-transparent border-0 border-b-2 border-border rounded-none text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
          />
          {searchQuery && (
            <button onClick={() => onSearchQueryChange('')} className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground" aria-label="Clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {/* Recent searches (when query is empty) */}
        {!hasTyping && (
          <div className="mb-4">
            {recentSearches.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Clock className="w-3 h-3" />Recent</span>
                  <button onClick={clearRecentSearches} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"><Trash2 className="w-3 h-3" />Clear</button>
                </div>
                <div className="space-y-0.5">
                  {recentSearches.map((query, idx) => (
                    <button key={idx} onClick={() => handleRecentClick(query)} className="w-full px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/50 rounded-lg text-left flex items-center justify-between">
                      <span className="truncate">{query}</span>
                      <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <Search className="w-6 h-6 mx-auto text-muted-foreground/50 mb-1" />
                <p className="text-xs text-muted-foreground">Start typing to see suggestions</p>
              </div>
            )}
          </div>
        )}

        {/* Pinned industry (when typing, no industry selected) */}
        {hasTyping && !hasIndustry && pinnedIndustry && pinnedIndustryCategories.length > 0 && (
          <div className="mb-4">
            {pinnedLabel && <div className="mb-1.5 px-1"><span className="text-[10px] font-medium text-primary uppercase tracking-wider">{pinnedLabel}</span></div>}
            <button
              onClick={() => handleHeadlineClick(pinnedIndustry.id, 'industry')}
              className="w-full mb-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-xl flex items-center justify-between hover:bg-primary/10 transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-primary" />
                <span className="font-semibold text-primary text-xs">{pinnedIndustry.label}</span>
                <span className="text-[10px] text-primary/70">({pinnedIndustry.count})</span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-primary" />
            </button>
            <div className="grid grid-cols-2 gap-1.5">
              {pinnedIndustryCategories.slice(0, 6).map((cat) => (
                <button key={cat.id} onClick={() => handleCategoryGridClick(cat.id, pinnedIndustry.id)} disabled={cat.count === 0}
                  className={cn('h-8 px-2.5 rounded-lg text-[11px] font-medium transition-colors flex items-center gap-1 min-w-0', cat.count > 0 ? 'bg-card text-foreground border border-border hover:bg-muted' : 'bg-muted/50 text-muted-foreground/50 cursor-not-allowed')}>
                  <span className="truncate flex-1 text-left">{cat.label}</span>
                  <span className="text-primary font-semibold tabular-nums shrink-0">{cat.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Browse industries (when typing, no industry selected) */}
        {hasTyping && !hasIndustry && (
          <>
            <div className="mb-3 py-1.5 px-2 rounded-lg bg-muted/50">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />Browse Industries</span>
            </div>
            {sortedIndustries.filter((ind) => ind.id !== pinnedIndustry?.id && ind.count > 0).slice(0, 5).map((item) => (
              <div key={item.id} className="mb-3">
                <button onClick={() => handleHeadlineClick(item.id, 'industry')} className="w-full mb-1.5 px-2.5 py-1.5 bg-muted/30 rounded-lg flex items-center justify-between hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-foreground text-xs">{item.label}</span>
                    <span className="text-[10px] bg-muted px-1 py-0.5 rounded text-muted-foreground">{item.count}</span>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            ))}
          </>
        )}

        {/* Category suggestions (when industry selected) */}
        {hasIndustry && !hasCategory && (
          <>
            <div className="mb-3 py-1.5 px-2 rounded-lg bg-primary/5 border border-primary/10">
              <span className="text-xs font-medium text-primary">{selectedIndustryIds.map((id) => getIndustryLabel(id)).join(' + ')}</span>
            </div>
            <div className="mb-3 py-1.5 px-2 rounded-lg bg-muted/50">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Tag className="w-3.5 h-3.5" />Categories</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {categoryFacets.filter((c) => c.count > 0).sort((a, b) => computeCategoryRelevance(b, debouncedQuery) - computeCategoryRelevance(a, debouncedQuery)).map((cat) => (
                <button key={cat.id} onClick={() => handleHeadlineClick(cat.id, 'category')} className="h-8 px-2.5 rounded-lg text-[11px] font-medium transition-colors flex items-center gap-1 min-w-0 bg-card text-foreground border border-border hover:bg-muted">
                  <span className="truncate flex-1 text-left">{cat.label}</span>
                  <span className="text-primary font-semibold tabular-nums shrink-0">{cat.count}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Subcategory suggestions (when category selected) */}
        {hasCategory && (
          <>
            <div className="mb-3 py-1.5 px-2 rounded-lg bg-primary/5 border border-primary/10">
              <span className="text-xs font-medium text-primary">
                {selectedIndustryIds.map((id) => getIndustryLabel(id)).join(' + ')} › {selectedCategoryIds.map((id) => getCategoryLabel(id)).join(' + ')}
              </span>
            </div>
            <div className="mb-3 py-1.5 px-2 rounded-lg bg-muted/50">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Layers className="w-3.5 h-3.5" />Subcategories</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {subcategoryFacets.filter((s) => s.count > 0).map((sub) => (
                <button key={sub.id} onClick={() => { onAddSubcategory(sub.id); saveToRecent(searchQuery); onExecuteSearch(searchQuery); }}
                  className="h-8 px-2.5 rounded-lg text-[11px] font-medium transition-colors flex items-center gap-1 min-w-0 bg-card text-foreground border border-border hover:bg-muted">
                  <span className="truncate flex-1 text-left">{sub.label}</span>
                  <span className="text-primary font-semibold tabular-nums shrink-0">{sub.count}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer CTA */}
      <div className="shrink-0 border-t border-border px-4 py-3">
        <button data-panel-search="true" onClick={handleSearchExecute} className="w-full py-2 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5">
          <Search className="w-3.5 h-3.5" />Search Deals
        </button>
      </div>
    </div>
  );
};

export default SearchPanelContent;
